import React, { useId, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { INTERNAL_SECTION, SECTIONS, sectionProgress } from './orgModel';
import '@/styles/org-form.css';

/* ---------------------------------------------------------------- inputs */

function TagInput({ id, value, onChange, placeholder, describedBy, invalid }) {
  const [draft, setDraft] = useState('');
  const add = (raw) => {
    const items = String(raw).split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
    if (!items.length) return;
    const next = [...value];
    items.forEach((i) => { if (!next.some((n) => n.toLowerCase() === i.toLowerCase())) next.push(i); });
    onChange(next);
    setDraft('');
  };
  return (
    <div className="of-tags" data-invalid={invalid || undefined}>
      {value.map((t) => (
        <span key={t} className="of-tag">
          {t}
          <button type="button" className="of-tag-x" onClick={() => onChange(value.filter((x) => x !== t))} aria-label={`Remove ${t}`}>
            <X aria-hidden="true" />
          </button>
        </span>
      ))}
      <input
        id={id}
        className="of-tags-input"
        value={draft}
        placeholder={value.length ? '' : placeholder}
        aria-describedby={describedBy}
        onChange={(e) => (e.target.value.includes(',') ? add(e.target.value) : setDraft(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); add(draft); }
          else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => draft.trim() && add(draft)}
      />
    </div>
  );
}

function ChipPicker({ labelId, presets, value, onChange }) {
  const [draft, setDraft] = useState('');
  const custom = value.filter((v) => !presets.includes(v));
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const addCustom = () => {
    const v = draft.trim();
    if (v && !value.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div className="of-chips" role="group" aria-labelledby={labelId}>
      {[...presets, ...custom].map((p) => (
        <button key={p} type="button" className="of-chip" aria-pressed={value.includes(p)} onClick={() => toggle(p)}>{p}</button>
      ))}
      <span className="of-chip-add">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder="Add other"
          aria-label="Add another option"
        />
        <button type="button" onClick={addCustom} aria-label="Add" disabled={!draft.trim()}><Plus aria-hidden="true" /></button>
      </span>
    </div>
  );
}

function YesNo({ labelId, value, onChange }) {
  const opts = [[true, 'Yes'], [false, 'No'], [null, 'Not sure']];
  return (
    <div className="of-yesno" role="radiogroup" aria-labelledby={labelId}>
      {opts.map(([v, label]) => (
        <button key={label} type="button" role="radio" aria-checked={value === v} className="of-yesno-btn" onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- field */

function Field({ field, section, values, set, options, error, idPrefix }) {
  const id = useId();
  const labelId = `${id}-label`;
  const errId = `${id}-error`;
  const hintId = `${id}-hint`;
  const value = values[field.key];
  const describedBy = [field.hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined;
  const common = { id, 'aria-describedby': describedBy, 'aria-invalid': error ? 'true' : undefined };
  const presets = field.options ? options[field.options] || [] : [];
  let control;

  switch (field.type) {
    case 'select':
      control = (
        <select {...common} className="of-input" value={value ?? ''} onChange={(e) => set(field.numeric ? e.target.value : e.target.value)}>
          <option value="">Not specified</option>
          {(field.choices || presets.map((p) => [p, p])).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      );
      break;
    case 'suggest':
      control = (
        <>
          <input {...common} className="of-input" list={`${id}-list`} value={value ?? ''} maxLength={120} onChange={(e) => set(e.target.value)} />
          <datalist id={`${id}-list`}>{presets.map((p) => <option key={p} value={p} />)}</datalist>
        </>
      );
      break;
    case 'tags':
      control = <TagInput id={id} value={value || []} onChange={set} placeholder={field.placeholder} describedBy={describedBy} invalid={!!error} />;
      break;
    case 'chips':
      control = <ChipPicker labelId={labelId} presets={presets} value={value || []} onChange={set} />;
      break;
    case 'yesno':
      control = <YesNo labelId={labelId} value={value} onChange={set} />;
      break;
    case 'textarea':
      control = <textarea {...common} className="of-input of-textarea" rows={3} value={value ?? ''} placeholder={field.placeholder} onChange={(e) => set(e.target.value)} />;
      break;
    case 'number':
      control = <input {...common} className="of-input" type="number" inputMode="numeric" min={field.min} max={field.max} value={value ?? ''} placeholder={field.placeholder} onChange={(e) => set(e.target.value)} />;
      break;
    default:
      control = (
        <input
          {...common}
          className="of-input"
          type={field.type === 'url' ? 'url' : field.type === 'email' ? 'email' : 'text'}
          value={value ?? ''}
          maxLength={field.max}
          placeholder={field.placeholder}
          autoComplete="off"
          onChange={(e) => set(e.target.value)}
        />
      );
  }

  const labelled = field.type === 'chips' || field.type === 'yesno';
  return (
    <div className={`of-field ${field.wide ? 'of-field--wide' : ''}`} data-field={`${idPrefix}${section}.${field.key}`}>
      {labelled ? <span id={labelId} className="of-label">{field.label}</span> : <label htmlFor={id} className="of-label">{field.label}</label>}
      {control}
      {field.hint && <span id={hintId} className="of-hint">{field.hint}</span>}
      {error && <span id={errId} className="of-error" role="alert">{error}</span>}
    </div>
  );
}

/* --------------------------------------------------------------- sections */

function Section({ section, form, setInsight, options, errors, forceOpen }) {
  const values = form.insights[section.key] || {};
  const { filled, total } = sectionProgress(section, values);
  const hasError = Object.keys(errors).some((k) => k.startsWith(`insights.${section.key}.`));
  const [open, setOpen] = useState(section.key === 'identity');
  const isOpen = open || forceOpen || hasError;
  return (
    <section className="of-section" data-testid={`org-section-${section.key}`}>
      <button type="button" className="of-section-head" aria-expanded={isOpen} onClick={() => setOpen(!isOpen)}>
        <span className="of-section-title">{section.title}</span>
        <span className={`of-progress ${filled === total ? 'of-progress--done' : ''}`}>{filled} of {total} filled</span>
        <span className="of-caret" aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="of-section-body">
          {section.hint && <p className="of-section-hint">{section.hint}</p>}
          <div className="of-grid">
            {section.fields.filter((f) => !f.showIf || f.showIf(values)).map((f) => (
              <Field
                key={f.key}
                field={f}
                section={section.key}
                idPrefix="insights."
                values={values}
                set={(v) => setInsight(section.key, f.key, v)}
                options={options}
                error={errors[`insights.${section.key}.${f.key}`]}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

const CORE_FIELDS = (options) => [
  { key: 'company_name', label: 'Company name', type: 'text', max: 200, wide: true },
  { key: 'industry', label: 'Industry', type: 'select', choices: (options.industries || []).map((o) => [o, o]) },
  { key: 'region', label: 'Primary region', type: 'select', choices: (options.regions || []).map((o) => [o, o]) },
  { key: 'company_size', label: 'Company size', type: 'select', choices: (options.company_sizes || []).map((o) => [o, o]) },
  { key: 'num_employees', label: 'Employees', type: 'number', min: 0 },
  { key: 'applied_policies', label: 'Compliance frameworks and regulations', type: 'chips', options: 'policies', wide: true },
  { key: 'security_solutions', label: 'Security tools deployed', type: 'chips', options: 'security_solutions', hint: 'Detection rules are generated for these.', wide: true },
];

/**
 * The full organization form: core profile on top (always visible, drives
 * threat matching), then collapsible insight sections with completeness counts.
 *   form: state from emptyOrg()/fromProfile(); onChange(nextForm)
 *   errors: from fieldErrors(); showInternal: operator-only notes (admin)
 */
export default function OrgForm({ form, onChange, options, errors = {}, showInternal = false, children }) {
  const setCore = (key, value) => onChange({ ...form, [key]: value });
  const setInsight = (section, key, value) => onChange({
    ...form,
    insights: { ...form.insights, [section]: { ...form.insights[section], [key]: value } },
  });
  const opts = { policies: ['GDPR', 'HIPAA', 'PCI-DSS', 'SOC2', 'ISO 27001', 'NIST'], security_solutions: ['SIEM', 'EDR', 'IDS/IPS', 'Firewall', 'Antivirus', 'DLP'], ...options };
  const sections = showInternal ? [...SECTIONS, INTERNAL_SECTION] : SECTIONS;

  return (
    <div className="of-form">
      <section className="of-core" aria-label="Core profile">
        {children}
        <div className="of-grid">
          {CORE_FIELDS(opts).map((f) => (
            <Field
              key={f.key}
              field={f}
              section="core"
              idPrefix=""
              values={form}
              set={(v) => setCore(f.key, v)}
              options={opts}
              error={errors[f.key]}
            />
          ))}
        </div>
      </section>
      <div className="of-sections">
        {sections.map((s) => (
          <Section key={s.key} section={s} form={form} setInsight={setInsight} options={opts} errors={errors} />
        ))}
      </div>
    </div>
  );
}
